<template>
	<StandardModal
		open
		:title="titleText"
		@cancel="$emit('cancel')"
		ref="newImportModal"
	>
		<div class="upload-modal-wrapper">
			<div
				v-if="!showOptions"
				class="upload-desc-link"
				v-html="
					$t('admin_imports_upload_link_desc', {
						DocUrl:
							'https://developers.pronto.io/docs/pronto-csv-imports/u6cs6s2vo045j-csv-file-formats',
					})
				"
			></div>
			<div
				class="upload-dropzone"
				v-if="!showOptions"
				:class="{ blur: over }"
				@dragenter.prevent="over = true"
				@dragover.prevent="over = true"
				@dragleave.prevent="over = false"
				@drop.prevent="addFile"
			>
				<FilesUploadIcon class="files-upload-icon" />
				<div class="upload-dropzone-desc">
					{{ $t('admin_imports_upload_drop_desc') }}
				</div>
				<label for="file-input" class="cursor browse-label">
					{{ $t('admin_imports_upload_browse_button') }}
				</label>

				<input
					id="file-input"
					type="file"
					multiple
					ref="fileInput"
					accept=".csv"
					@change="addFile"
				/>
			</div>
			<div v-if="!showOptions" class="file-list">
				<div
					v-for="fileObj in files"
					class="import-item"
					:key="fileObj.file.name"
				>
					<div class="file-description">
						<div class="file-name">{{ fileObj.file.name }}</div>
						<div class="file-size">
							{{ fileSize(fileObj.file.size) }} &middot;
							{{ modified(fileObj.file.lastModified) }}
						</div>
					</div>
					<div class="remove-file cursor" @click="removeFile(fileObj)">
						<CloseIcon color="#4F5053" />
					</div>
					<div v-if="fileObj.status === 'uploading'" class="progress-bar">
						<div
							:style="{ width: fileObj.progress + '%' }"
							class="progress-bar-fill"
						></div>
					</div>
				</div>
			</div>
			<div v-if="showOptions" class="import-options">
				<div v-if="hasUsersFile || hasMembershipsFile" class="country-code">
					<div>
						<div class="title">
							{{ $t('admin_imports_options_country_title') }}
						</div>
						<Tippy
							arrow
							theme="large"
							:content="
								$t('admin_imports_options_country_desc', {
									PhoneFormatUrl: 'https://support.pronto.io/articles',
								})
							"
							:options="{ animation: 'scale', allowHTML: true }"
						>
							<CircleQuestionIcon
								type="regular"
								class="help-icon"
							></CircleQuestionIcon>
						</Tippy>
					</div>
					<CountryPicker @change="country = $event" />
				</div>

				<div v-if="hasUsersFile" class="welcome-messages">
					<Checkbox v-model="welcomeEmails">
						{{ $t('admin_imports_options_welcome_checkbox') }}
					</Checkbox>
					<Tippy
						arrow
						theme="large"
						:content="$t('admin_imports_options_welcome_desc')"
						:options="{ animation: 'scale' }"
					>
						<CircleQuestionIcon
							type="regular"
							class="help-icon"
						></CircleQuestionIcon>
					</Tippy>
				</div>

				<div class="email-notifications">
					<Checkbox v-model="enableNotifications">
						{{ $t('admin_imports_options_enable_notifications') }}
					</Checkbox>
					<Tippy
						arrow
						theme="large"
						:content="$t('admin_imports_options_notifications_tip')"
						:options="{ animation: 'scale' }"
					>
						<CircleQuestionIcon
							type="regular"
							class="help-icon"
						></CircleQuestionIcon>
					</Tippy>
					<div :class="{ hidden: !enableNotifications }" class="description">
						{{ $t('admin_imports_options_notifications_desc') }}
					</div>
					<textarea
						v-model="emails"
						:class="{ hidden: !enableNotifications }"
					></textarea>
				</div>
			</div>
		</div>
		<template #footer-right>
			<Button @click="showOptions = false" v-if="showOptions">
				{{ $t('common_button_back') }}
			</Button>
			<Button
				primary
				:disabled="disabled"
				:loading="isCreating"
				@click="confirm"
			>
				{{ buttonText }}
			</Button>
		</template>
	</StandardModal>
</template>

<script>
import {
	StandardModal,
	Checkbox,
	CountryPicker,
	Tippy,
	Button,
} from '@/components/common'
import { FilesUploadIcon, CloseIcon, CircleQuestionIcon } from '@/icons'
import { getMyUser } from '@/store'
import { getFileSize, isValidEmail } from '@/helpers/utils'
import { getCancelToken } from '@/helpers/api'
import moment from 'moment'
import { uploadNew } from '@/api/upload'
import { post } from '../common'

export default {
	components: {
		StandardModal,
		FilesUploadIcon,
		CloseIcon,
		Checkbox,
		CountryPicker,
		Tippy,
		CircleQuestionIcon,
		Button,
	},

	data() {
		return {
			files: [],
			isUploading: false,
			uploadPercentage: 0,
			over: false,
			showOptions: false,
			welcomeEmails: false,
			enableNotifications: false,
			country: null,
			emails: null,
			modalError: null,
			isCreating: false,
		}
	},

	mounted() {
		//check for our email
		const { emails } = getMyUser()
		if (emails && emails.length > 0 && emails[0].email) {
			this.emails = emails[0].email
		}
	},

	computed: {
		disabled() {
			return this.files.length === 0 || !this.allFilesUploaded
		},

		buttonText() {
			return this.showOptions
				? this.$t('admin_imports_upload_import_button')
				: this.$t('common_button_next')
		},

		titleText() {
			return this.showOptions
				? this.$t('admin_imports_import_options_title')
				: this.$t('admin_imports_newimport_button')
		},

		allFilesUploaded() {
			return (
				this.files.length > 0 &&
				this.files.every((fileObj) => fileObj.status === 'uploaded')
			)
		},

		hasUsersFile() {
			return this.files.some((item) => item.name.includes('users'))
		},

		hasMembershipsFile() {
			return this.files.some((item) => item.name.includes('memberships'))
		},
	},

	methods: {
		addFile(e) {
			if (this.modalError) {
				this.modalError()
			}

			this.over = false
			let inputFiles = e.dataTransfer ? e.dataTransfer.files : e.target.files

			if (inputFiles.length > 4) {
				this.modalError = this.$refs.newImportModal.$alert.error(
					this.$t('admin_imports_too_many_files_error')
				)
				return
			}

			for (let file of inputFiles) {
				const regex = new RegExp(
					`^(users|groups|categories|memberships).*?\\.csv$`
				)
				if (!regex.test(file.name)) {
					this.modalError = this.$refs.newImportModal.$alert.error(
						this.$t('admin_imports_file_name_error', { Filename: file.name })
					)
					continue
				}

				// Test for empty files
				if (file.size === 0) {
					this.modalError = this.$refs.newImportModal.$alert.error(
						this.$t('admin_imports_empty_file_errors', { Filename: file.name })
					)
					continue
				}

				// Find an existing file with the same prefix and remove it
				const filePrefix = file.name.split('_')[0]
				const existingFileIndex = this.files.findIndex((f) =>
					f.file.name.startsWith(filePrefix)
				)
				if (existingFileIndex !== -1) {
					this.removeFile(this.files[existingFileIndex])
				}

				let fileObj = { file, status: 'pending', progress: 0, name: file.name }
				this.files.push(fileObj)
				let fileIndex = this.files.length - 1
				this.uploadFile(fileObj, fileIndex)
			}
		},

		async uploadFile(fileObj, fileIndex) {
			// Update fileObj status to 'uploading'
			this.updateFileAtIndex(fileIndex, { ...fileObj, status: 'uploading' })

			const cancelToken = getCancelToken()
			try {
				const { data } = await uploadNew(
					fileObj.file,
					(progressPct) => {
						// Update fileObj progress
						this.updateFileAtIndex(fileIndex, {
							...fileObj,
							progress: progressPct,
						})
					},
					cancelToken.token
				)
				// Update fileObj status to 'uploaded' and set key
				this.updateFileAtIndex(fileIndex, {
					...fileObj,
					status: 'uploaded',
					key: data.key,
				})
			} catch (error) {
				console.error('File upload failed:', error)
				// Update fileObj status to 'failed'
				this.updateFileAtIndex(fileIndex, { ...fileObj, status: 'failed' })
			}
		},

		updateFileAtIndex(index, newFileObj) {
			this.files.splice(index, 1, newFileObj)
		},

		async confirm() {
			if (this.modalError) {
				this.modalError()
			}
			this.modalError = null
			if (this.allFilesUploaded && this.showOptions) {
				if (this.modalError) {
					this.modalError()
				}

				let params = {
					send_welcome_messages: this.welcomeEmails,
				}

				if (this.enableNotifications) {
					params.completion_notifications = this.emails
						.split(',')
						.map((email) => email.trim())
				}

				if (this.country) {
					params.default_country_code = this.country
				}

				params.file_keys = this.files.map((file) => file.key)

				try {
					this.isCreating = true
					await post('/organization/import_batches', params)
					this.$emit('cancel')
				} catch (e) {
					this.isCreating = false
					if (e.response?.data?.errors) {
						const keys = Object.keys(e.response.data.errors)
						const errorCode = keys[0]
						this.modalError = this.$refs.newImportModal.$alert.error(
							e.response.data.errors[errorCode][0].message
						)
					} else if (e.response?.data?.message) {
						this.modalError = this.$refs.newImportModal.$alert.error(
							e.response.data.message
						)
					} else {
						this.modalError = this.$refs.newImportModal.$alert.error(
							this.$t('apierrors_error_generic')
						)
					}
				}
			} else {
				this.showOptions = true
			}
		},

		fileSize(size) {
			return getFileSize(size)
		},

		modified(date) {
			return moment(date).format('M/D/YY HH:mm:ss A')
		},

		removeFile(file) {
			const index = this.files.indexOf(file)
			if (index !== -1) {
				//if the file is still be uploaded to s3 we should cancel the upload
				if (file.status === 'uploading') {
					file.cancelToken?.cancel()
				}
				this.files.splice(index, 1)
			}
		},

		onCountryCodeChange({ countryCode }) {
			this.country = countryCode
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.upload-modal-wrapper {
	padding: 16px 44px 0 44px;
	:deep(.upload-desc-link) {
		padding: 0 105px 0 105px;
		color: var(--neutral-600);
		font-size: 14px;
		text-align: center;
		margin-bottom: 16px;
		a {
			text-decoration: none;
			color: var(--blue-300);
		}
	}

	:deep(.upload-dropzone) {
		@include flex-column-center;
		@include flex-center;
		border: 2px dashed $pronto-blue;
		border-radius: 10px;
		height: 157px;

		.files-upload-icon {
			width: 51px;
			height: 59px;
			margin-bottom: 20px;
		}

		.upload-dropzone-desc {
			font-size: 16px;
			color: var(--neutral-500);
		}

		.browse-label {
			color: var(--blue-300);
		}

		input {
			display: none;
		}

		&.blur {
			background-color: var(--blue-100);
		}
	}

	:deep(.file-list) {
		.file-description {
			margin-left: 10px;
			flex: 1;
		}

		.file-name {
			font-family: Avenir-Heavy;
			font-size: 16px;
			color: var(--neutral-800);
			margin-bottom: 3px;
		}

		.file-size {
			font-family: Avenir-Roman;
			font-size: 14px;
			color: var(--neutral-600);
		}

		.import-item {
			display: flex;
			border: 2px solid $outline-gray;
			border-radius: 4px;
			margin-top: 8px;
			padding: 8px 16px;
			background-color: #fdfdfd;
			cursor: pointer;
			position: relative;
		}

		.import-item:first-child {
			margin-top: 18px;
		}

		.import-item:hover {
			background-color: #fafafa;
		}

		.remove-file {
			display: flex;
			align-items: center;
			font-size: 21px;
			color: var(--neutral-400);
			&:hover {
				color: var(--blue-300);
			}
		}

		.progress-bar {
			width: 100%;
			height: 4px;
			background-color: var(--neutral-200);
			overflow: hidden;
			position: absolute;
			left: -1px;
			right: 0px;
			bottom: -1px;
		}

		.progress-bar-fill {
			height: 100%;
			background-color: var(--blue-300);
			transition: width 0.5s;
		}
	}

	:deep(.import-options) {
		.country-code {
			border-bottom: 1px solid #e2e4e6;
			padding: 18px 0 32px 0;
			div:first-of-type {
				display: flex;
				align-items: center;
			}
			.country-select {
				margin-top: 20px;
			}
		}

		.help-icon {
			font-size: 16px;
			color: var(--blue-300);
			cursor: pointer;
			margin-left: 15px;
		}

		.welcome-messages {
			padding: 32px 0;
			border-bottom: 1px solid #e2e4e6;
		}

		.email-notifications {
			padding-top: 32px;
			.description {
				margin-top: 18px;
			}
			textarea {
				height: 100px;
				width: 100%;
				border: 1px solid var(--neutral-200);
				border-radius: 8px;
				resize: none;
				font-family: Avenir-Roman;
				font-size: 16px;
				color: var(--neutral-800);
				padding: 8px 14px;
				outline: none;
			}
			.hidden {
				display: none;
			}
		}

		.title,
		.pronto-checkbox {
			font-family: Avenir-Black;
			font-size: 16px;
			color: var(--neutral-800);
		}

		.description {
			font-family: Avenir-Medium;
			font-size: 16px;
			color: var(--neutral-600);
			margin-bottom: 24px;
		}

		a {
			text-decoration: none;
			color: var(--blue-300);
		}
	}

	:deep(.vue-phone-number-input) {
		.vue-recycle-scroller.ready .vue-recycle-scroller__item-view {
			position: unset;
		}
	}
}
</style>
